"use client";

import { Badge, CardDescription, Field, Label, Separator } from "@atlas/core";
import { useSessionData, useTranslation } from "@atlas/sdk";
import { CdvType } from "@enums";
import type { IHubCatalogType, IHubCdv } from "@models";
import {
  HUB_CATALOG_TYPES_DEFAULT_SORT,
  useHubCatalogTypes,
  useHubCdv,
} from "@query";
import { ExternalLink } from "lucide-react";
import { useMemo } from "react";

interface ICatalogMetadataProps {
  createdBy?: string | null;
  version?: string | number;
  typeId?: string;
  supportedCloud?: string[];
  tierId?: string;
  documentationLocations?: string[];
}

function ConfigRow({
  label,
  value,
  isLast,
}: {
  label: string;
  value: React.ReactNode;
  isLast?: boolean;
}) {
  return (
    <Field orientation="vertical" className="gap-1">
      <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground/80">
        {label}
      </Label>
      <CardDescription className="text-foreground">
        {value != null && value !== "" ? value : "-"}
      </CardDescription>
      {!isLast && <Separator className="mt-3" decorative />}
    </Field>
  );
}

function ConfigLinksRow({
  label,
  urls,
  isLast,
}: {
  label: string;
  urls: string[];
  isLast?: boolean;
}) {
  return (
    <Field orientation="vertical" className="gap-1">
      <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground/80">
        {label}
      </Label>
      <CardDescription className="text-foreground">
        {urls.length > 0 ? (
          <ul className="space-y-2">
            {urls.map((url, index) => {
              const href = url.startsWith("http")
                ? url
                : `https://${url.replace(/^\/+/, "")}`;
              const displayUrl = url.replace(/^https?:\/\//, "").trim();
              return (
                <li key={`${url}-${index}`}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
                    <ExternalLink className="size-3.5 shrink-0" />
                    {displayUrl || href}
                  </a>
                </li>
              );
            })}
          </ul>
        ) : (
          "-"
        )}
      </CardDescription>
      {!isLast && <Separator className="mt-3" decorative />}
    </Field>
  );
}

export function CatalogMetadata({
  createdBy,
  version,
  typeId,
  supportedCloud = [],
  tierId,
}: ICatalogMetadataProps) {
  const { t } = useTranslation();
  const { userId: sessionUserId } = useSessionData();
  const { data: cdvs = [] } = useHubCdv();
  const { data: catalogTypes = [] } = useHubCatalogTypes({
    mode: "DATA",
    sort: [...HUB_CATALOG_TYPES_DEFAULT_SORT],
    startIndex: 0,
    limit: 100,
  });

  const cloudLabels = useMemo(() => {
    if (supportedCloud.length === 0) return "-";
    return (
      <div className="flex flex-wrap gap-2">
        {supportedCloud.map((name) => {
          const cloud = (cdvs as IHubCdv[]).find(
            (cdv) =>
              cdv.type === CdvType.Cloud &&
              (cdv.name === name || cdv.label === name),
          );
          const label = cloud?.label || cloud?.name || name;
          return (
            <Badge key={name} variant="secondary">
              {label}
            </Badge>
          );
        })}
      </div>
    );
  }, [cdvs, supportedCloud]);

  const tierLabel = useMemo(() => {
    if (!tierId) return "-";
    const tier = (cdvs as IHubCdv[]).find(
      (cdv) => cdv.type === CdvType.Tier && String(cdv.id) === tierId,
    );
    return tier?.label || tier?.name || tierId;
  }, [cdvs, tierId]);

  const catalogTypeLabel = useMemo(() => {
    if (!typeId) return "-";
    const type = (catalogTypes as IHubCatalogType[]).find(
      (ct) => String(ct.typeId) === typeId || ct.name === typeId,
    );
    return type?.label || type?.name || typeId;
  }, [catalogTypes, typeId]);

  const versionDisplay = version != null ? String(version) : "1";

  const tierDisplay = tierLabel
    ? t("hub.catalog_details.config_tier_desc", {
        tier: tierLabel,
        defaultValue: "This catalog is available at the {{tier}} tier.",
      })
    : "-";

  const createdByDisplay = createdBy?.trim() || sessionUserId || "-";

  const configItems: Array<{
    key: string;
    label: string;
    type: "normal" | "links";
    value?: React.ReactNode;
    linkUrls?: string[];
  }> = [
    {
      key: "createdBy",
      label: t("hub.catalog_details.created_by", {
        defaultValue: "Created By",
      }),
      type: "normal",
      value: createdByDisplay,
    },
    {
      key: "version",
      label: t("hub.home.solution_version_label", { defaultValue: "Version" }),
      type: "normal",
      value: versionDisplay,
    },
    {
      key: "compatibleSolutions",
      label: t("hub.catalog_details.compatible_solutions", {
        defaultValue: "Compatible Solutions",
      }),
      type: "normal",
      value: catalogTypeLabel,
    },
    {
      key: "compatibleCloud",
      label: t("hub.catalog_details.compatible_cloud", {
        defaultValue: "Compatible Cloud",
      }),
      type: "normal",
      value: cloudLabels,
    },

    {
      key: "tier",
      label: t("hub.catalog_details.config_tier", { defaultValue: "Tier" }),
      type: "normal",
      value: tierDisplay,
    },
  ];

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="space-y-4 grid grid-cols-1 gap-x-8 sm:grid-cols-2">
        {configItems.map((item) => {
          return (
            <div key={item.key} className="py-3 first:pt-0">
              {item.type === "links" ? (
                <ConfigLinksRow
                  label={item.label}
                  urls={item.linkUrls ?? []}
                  isLast={false}
                />
              ) : (
                <ConfigRow
                  label={item.label}
                  value={item.value}
                  isLast={false}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
